import React from 'react';
import { CompareViewMode, CompareShowSections } from '@/types/compare.types';
import { RefreshCw, Download, Check } from 'lucide-react';
import { Button, IconButton } from '../ui/Button';

interface CompareControlsProps {
  viewMode: CompareViewMode;
  showSections: CompareShowSections;
  syncScroll: boolean;
  highlightChanges: boolean;
  onViewModeChange: (mode: CompareViewMode) => void;
  onToggleSection: (section: keyof CompareShowSections) => void;
  onSyncScrollChange: (sync: boolean) => void;
  onHighlightChange: (highlight: boolean) => void;
  onSwapSlots: () => void;
  onExport?: () => void;
}

export const CompareControls: React.FC<CompareControlsProps> = ({
  viewMode, showSections, syncScroll, highlightChanges,
  onViewModeChange, onToggleSection, onSyncScrollChange, onHighlightChange, onSwapSlots, onExport
}) => {
  return (
    <div className="flex flex-wrap items-center gap-6 px-6 py-3 bg-slate-900 border-b border-slate-800">
      <div className="flex items-center gap-3">
        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">View Mode</span>
        <div className="flex bg-slate-950 p-1 rounded-lg border border-slate-800">
          {(['split', 'unified', 'overlay'] as CompareViewMode[]).map(mode => (
            <Button
              key={mode}
              variant="ghost"
              size="xs"
              onClick={() => onViewModeChange(mode)}
              className={`px-3 py-1.5 rounded-md text-[11px] font-bold uppercase ${viewMode === mode ? 'bg-slate-800 text-accent shadow-sm' : 'text-slate-400 hover:text-slate-300'}`}
            >
              {mode}
            </Button>
          ))}
        </div>
      </div>

      <div className="h-6 w-px bg-slate-800" />

      <div className="flex items-center gap-4">
        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Visible Layers</span>
        <div className="flex flex-wrap gap-3">
          {Object.entries(showSections).map(([key, value]) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer group">
              <input type="checkbox" checked={value} onChange={() => onToggleSection(key as keyof CompareShowSections)} className="sr-only" />
              <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-all ${value ? 'bg-accent border-accent text-slate-950' : 'bg-slate-950 border-slate-700 group-hover:border-slate-500'}`}>
                {value && <Check size={10} strokeWidth={4} />}
              </div>
              <span className={`text-[11px] font-bold uppercase ${value ? 'text-slate-200' : 'text-slate-400 group-hover:text-slate-400'}`}>
                {key.replace('sunoTags', 'Tags')}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="h-6 w-px bg-slate-800 ml-auto" />

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={syncScroll} onChange={e => onSyncScrollChange(e.target.checked)} className="sr-only" />
          <div className={`w-8 h-4 rounded-full relative transition-colors ${syncScroll ? 'bg-accent' : 'bg-slate-800'}`}>
            <div className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform ${syncScroll ? 'translate-x-4' : ''}`} />
          </div>
          <span className="text-[11px] font-bold text-slate-400 uppercase">Sync Scroll</span>
        </label>

        <IconButton
          variant="ghost"
          size="sm"
          onClick={onSwapSlots}
          title="Swap A ↔ B"
          className="text-slate-400 hover:text-slate-50"
        >
          <RefreshCw size={14} />
        </IconButton>

        {onExport && (
          <IconButton
            variant="ghost"
            size="sm"
            onClick={onExport}
            title="Export JSON Diff"
            className="text-slate-400 hover:text-accent"
          >
            <Download size={14} />
          </IconButton>
        )}
      </div>
    </div>
  );
};

export default CompareControls;
